// src/pages/pedidos/Pedidos.js
import React, { useEffect, useState, useCallback } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import { toast } from 'react-toastify'
import api from 'api/api'

import {
	PEDIDOS_FETCH_CUANTOS,
	OPCIONES_TIPOS_PEDIDO_PARA_FILTRO,
	OPCIONES_PAGINACION_NUM_RESULTADOS,
	getTipoColor,
	calcularEstadoPrincipal,
	getConfigEstado,
	getAccionesDisponibles,
	calcularEstados
} from 'config/pedidos'
import { debounce } from 'utils/debounce'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { getDisplayDate } from 'utils/date'
import Paginacion from 'components/Paginacion'
import { LoadingSpinner } from 'components/LoadingSpinner'
import TopHeader from 'components/TopHeader'

import NuevoPedido from './NuevoPedido'
import VerPedido from './VerPedido'
import EditarPedido from './EditarPedido'
import EliminarPedido from './EliminarPedido'
import CancelarPedido from './CancelarPedido'

import './pedidos.css'

function Pedidos() {
	const [fetchDeACuantos, setFetchDeACuantos] = useState(PEDIDOS_FETCH_CUANTOS)
	const [numResultados, setNumResultados] = useState(0)
	const [paginaAct, setPaginaAct] = useState(1)
	const [loading, setLoading] = useState(false)

	const [busqueda, setBusqueda] = useState('')
	const [filtroBusqueda, setFiltroBusqueda] = useState('')
	const [filtroTipo, setFiltroTipo] = useState('')
	const [pedidos, setPedidos] = useState([])
	const [configuracion, setConfiguracion] = useState(null)

	const [accionActual, setAccionActual] = useState({
		pedido: null,
		accion: ''
	})

	const opcionesCuantosMostrar = OPCIONES_PAGINACION_NUM_RESULTADOS

	const refreshPedidos = useCallback(async () => {
		try {
			setLoading(true)

			// Construir filtros
			const filtros = {
				// Para pedidos actuales: que NO estén finalizados Y que NO estén cancelados
				finalizado: 0 // Usar 0 en lugar de false para coincidir con el servidor
			}

			// Agregar filtro de búsqueda si existe
			if (filtroBusqueda) {
				filtros.busqueda = filtroBusqueda
			}

			// Agregar filtro de tipo si está seleccionado
			if (filtroTipo) {
				filtros.tipo = filtroTipo
			}

			const { datos } = await api.post('a/ver-pedidos', {
				cuantos: fetchDeACuantos,
				desde: paginaAct === 1 ? 0 : (paginaAct - 1) * fetchDeACuantos,
				filtros
			})

			// Procesar los pedidos para calcular estados
			const pedidosProcesados = (datos.pedidos || []).map(pedido => {
				const estados = calcularEstados(pedido)
				const estadoPrincipal = calcularEstadoPrincipal(pedido)

				return {
					...pedido,
					estados,
					estadoPrincipal
				}
			})

			setPedidos(pedidosProcesados)
			setNumResultados(datos.numResultados || 0)
		} catch (error) {
			toast.error('Error de conexión')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}, [filtroBusqueda, fetchDeACuantos, paginaAct, filtroTipo])

	const getConfiguracion = async () => {
		try {
			const { datos } = await api.get('a/config-pedidos')
			setConfiguracion(datos)
		} catch (error) {
			toast.error('Error al cargar configuración de pedidos')
			console.error(error)
		}
	}

	const handleBusqueda = (value) => {
		setFiltroBusqueda(value || '')
	}

	const debouncedFiltroBusqueda = React.useCallback(
		debounce(handleBusqueda, 400),
		[]
	)

	useEffect(() => {
		debouncedFiltroBusqueda(busqueda)
	}, [busqueda])

	useEffect(() => {
		if (paginaAct === 1) {
			refreshPedidos()
		} else {
			setPaginaAct(1)
		}
	}, [filtroBusqueda, fetchDeACuantos, filtroTipo])

	useEffect(() => {
		if (!loading) {
			refreshPedidos()
		}
	}, [paginaAct])

	useEffect(() => {
		getConfiguracion()
		refreshPedidos()
	}, [])

	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return "₲ 0"
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	const nuevoPedido = useCallback(async (data) => {
		try {
			const { msg } = await api.post('a/pedidos', data)
			refreshPedidos()
			setAccionActual({ pedido: null, accion: '' })
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al crear el pedido')
		}
	}, [refreshPedidos])

	const editarPedido = useCallback(async (data) => {
		try {
			const { msg } = await api.put('a/pedidos/' + accionActual.pedido.id, data)
			refreshPedidos()
			setAccionActual({ pedido: null, accion: '' })
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al actualizar el pedido')
		}
	}, [accionActual.pedido?.id, refreshPedidos]) // ✅ Agregar dependencia

	const eliminarPedido = useCallback(async (id) => {
		try {
			const { msg } = await api.delete('a/pedidos/' + id)
			refreshPedidos()
			setAccionActual({ pedido: null, accion: '' })
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al eliminar el pedido')
		}
	}, [refreshPedidos])

	const cancelarPedido = useCallback(async (id, motivo) => {
		try {
			const { msg } = await api.post(`a/pedidos/${id}/cancelar`, { cancelado: true, motivo })
			refreshPedidos()
			setAccionActual({ pedido: null, accion: '' })
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al cancelar el pedido')
		}
	}, [refreshPedidos])

	const cambiarEstado = useCallback(async (id, estado) => {
		try {
			const { msg } = await api.post(`a/pedidos/${id}/estado`, estado)
			refreshPedidos()
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al cambiar el estado del pedido')
		}
	}, [refreshPedidos])

	const cambiarEstadoPago = useCallback(async (id, pagado) => {
		try {
			const { msg } = await api.post(`a/pedidos/${id}/pagado`, { pagado })
			refreshPedidos()
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al cambiar el estado de pago')
		}
	}, [refreshPedidos])

	const cerrarAccion = useCallback(() => {
		setAccionActual({
			pedido: null,
			accion: ''
		})
	}, [])

	const accionesActuales = {
		handleVer: useCallback((pedido) => {
			setAccionActual({
				pedido,
				accion: 'ver'
			})
		}, []),

		handleEditar: useCallback((pedido) => {
			console.log('🎯 Pasando pedido original a EditarPedido:', pedido.id)
			setAccionActual({
				pedido: pedido,
				accion: 'editar'
			})
		}, []), // ✅ Sin dependencia de normalizarPedidoParaEditar

		handleEliminar: useCallback((pedido) => {
			setAccionActual({
				pedido,
				accion: 'eliminar'
			})
		}, []),

		handleCancelar: useCallback((pedido) => {
			setAccionActual({
				pedido,
				accion: 'cancelar'
			})
		}, [])
	}

	const renderEstadoPrincipal = (pedido) => {
		const estadoPrincipal = pedido.estadoPrincipal
		const config = getConfigEstado(estadoPrincipal)

		if (!config) {
			return <Badge bg="secondary">Estado desconocido</Badge>
		}

		return (
			<Badge bg={config.color} className="estado-principal">
				<i className={`fa ${config.icon} me-1`}></i>
				{config.label}
			</Badge>
		)
	}

	const renderEstadoPago = (pedido) => {
		// Usar estados normalizados
		const estados = calcularEstados(pedido)

		if (estados.pagado) {
			return (
				<Badge bg="success" className="estado-pago">
					<i className="fa fa-check me-1"></i>
					Sí
				</Badge>
			)
		} else {
			return (
				<Badge bg="outline-secondary" text="dark" className="estado-pago">
					<i className="fa fa-times me-1"></i>
					No
				</Badge>
			)
		}
	}

	const ejecutarAccion = async (pedido, accion) => {
		switch (accion.key) {
			case 'cancelar':
				accionesActuales.handleCancelar(pedido)
				break
			case 'pagar':
				await cambiarEstadoPago(pedido.id, true)
				break
			case 'despagar':
				await cambiarEstadoPago(pedido.id, false)
				break
			default:
				// Es un cambio de estado principal
				await cambiarEstado(pedido.id, { campo: accion.key, valor: true })
				break
		}
	}

	// render
	return (
		<Container fluid id="pedidos" className="vh-100">
			<TopHeader
				iconName="list-check"
				text="Pedidos Actuales"
			/>

			{/* Filtros */}
			<Row className="d-flex no-gutters mb-3">
				<Col className="mt-2">
					<Form>
						<Row id="searchbar" className="justify-content-between align-items-center g-2">
							<Col sm={6} md={2} style={{ display: 'block' }}>
								<small>
									<span className="d-none d-sm-inline-block">Mostrar de a</span>
									<Form.Select size="sm" value={fetchDeACuantos} onChange={(e) => setFetchDeACuantos(e.target.value)} className="mx-1">
										{opcionesCuantosMostrar.map((o) => (
											<option value={o} key={`mostrar-${o}`}>
												{o}
											</option>
										))}
									</Form.Select>
									<span className="ms-2">pedidos</span>
								</small>
							</Col>

							<Col md={4} lg={3}>
								<Form.Group controlId="txtBusqueda">
									<InputGroup>
										<Form.Control
											autoFocus
											value={busqueda}
											onChange={(e) => setBusqueda(e.target.value)}
											type="text"
											placeholder="Buscar por cliente, ID, teléfono..."
											aria-label="Buscar"
										/>
										<InputGroup.Text><i className="fa fa-search"></i></InputGroup.Text>
									</InputGroup>
								</Form.Group>
							</Col>

							<Col md={3} lg={2}>
								<Form.Group controlId="selectTipo">
									<Form.Select
										value={filtroTipo}
										onChange={(e) => setFiltroTipo(e.target.value)}
									>
										<option value="">Todos los tipos</option>
										{OPCIONES_TIPOS_PEDIDO_PARA_FILTRO.map(tipo => (
											<option key={tipo.value} value={tipo.value}>
												{tipo.label}
											</option>
										))}
									</Form.Select>
								</Form.Group>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>

			{/* Botón para Nuevo pedido */}
			<Row className="mb-3">
				<Col>
					<Button
						variant="primary"
						onClick={() => setAccionActual({
							pedido: null,
							accion: 'nuevo'
						})}
					>
						<i className="fa fa-plus"></i> Nuevo pedido
					</Button>
				</Col>
			</Row>

			{/* Tabla de pedidos */}
			<Row>
				<Col>
					<div className="pedidos-tabla">
						<small>Total resultados: {loading ? (<span className="ms-2"><LoadingSpinner size='sm' /></span>) : (<strong>{numResultados}</strong>)}</small>

						<Table striped bordered hover responsive className="mt-2">
							<thead>
								<tr>
									<th>ID</th>
									<th>Cliente</th>
									<th>Tipo</th>
									<th>Estado</th>
									<th>Pagado</th>
									<th>Total</th>
									<th>Fecha</th>
									<th>Acciones</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan="8" className="text-center py-4">
											<LoadingSpinner />
										</td>
									</tr>
								) : (
									pedidos.length > 0 ? pedidos.map(pedido => (
										<tr key={pedido.id}>
											<td>#{pedido.id}</td>
											<td>
												<div className="cliente-info">
													<div className="cliente-nombre">
														{pedido.usuario ?
															`${pedido.usuario.nombre} ${pedido.usuario.apellido}` :
															(pedido.datosFacturacion?.razonSocial || 'Cliente no especificado')
														}
													</div>
													{pedido.usuario?.telefono && (
														<div className="cliente-telefono">
															<i className="fa fa-phone me-1"></i>
															{pedido.usuario.telefono}
														</div>
													)}
												</div>
											</td>
											<td>
												<Badge bg={getTipoColor(pedido.tipo)} className="badge-tipo">
													{OPCIONES_TIPOS_PEDIDO_PARA_FILTRO.find(t => t.value === pedido.tipo)?.label || pedido.tipo}
												</Badge>
											</td>
											<td>
												{renderEstadoPrincipal(pedido)}
											</td>
											<td className="text-center">
												{renderEstadoPago(pedido)}
											</td>
											<td>
												<span className="total-pedido">
													{formatearPrecio(pedido.montoTotal)}
												</span>
											</td>
											<td>
												<span className="fecha-pedido">
													{getDisplayDate(pedido.recibido)}
												</span>
											</td>
											<td>
												<DropdownButton
													as="div"
													title={<i className="fa fa-ellipsis-v"></i>}
													id={`dropdown-pedido-${pedido.id}`}
													size="sm"
													variant="outline-secondary"
												>
													<Dropdown.Item onClick={() => accionesActuales.handleVer(pedido)}>
														<i className="fa fa-eye me-2"></i>
														Ver detalles
													</Dropdown.Item>
													<Dropdown.Item onClick={() => accionesActuales.handleEditar(pedido)}>
														<i className="fa fa-pencil me-2"></i>
														Editar
													</Dropdown.Item>
													<Dropdown.Divider />

													{/* Acciones dinámicas basadas en el estado */}
													{getAccionesDisponibles(pedido).map((accion, index) => (
														<Dropdown.Item
															key={accion.key}
															onClick={() => ejecutarAccion(pedido, accion)}
															className={accion.key === 'cancelar' ? 'text-warning' : ''}
														>
															<i className={`fa ${accion.icon} me-2`}></i>
															{accion.label}
														</Dropdown.Item>
													))}

													<Dropdown.Divider />
													<Dropdown.Item
														onClick={() => accionesActuales.handleEliminar(pedido)}
														className="text-danger"
													>
														<i className="fa fa-trash me-2"></i>
														Eliminar
													</Dropdown.Item>
												</DropdownButton>
											</td>
										</tr>
									)) : (
										<tr>
											<td colSpan="8" className="text-center py-4">
												No se encontraron pedidos actuales con los filtros aplicados.
											</td>
										</tr>
									)
								)}
							</tbody>
						</Table>

						{/* Paginación */}
						{pedidos.length > 0 && (
							<Paginacion
								pagina={paginaAct}
								numPaginas={Math.ceil(numResultados / fetchDeACuantos) || 1}
								setPagina={setPaginaAct}
							/>
						)}
					</div>
				</Col>
			</Row>

			{/* Componentes para acciones CRUD */}
			<NuevoPedido
				mostrar={accionActual.accion === 'nuevo'}
				guardar={nuevoPedido}
				cancelar={cerrarAccion}
			/>

			<VerPedido
				mostrar={accionActual.accion === 'ver'}
				pedido={accionActual.pedido}
				cancelar={cerrarAccion}
			/>

			<EditarPedido
				mostrar={accionActual.accion === 'editar'}
				pedidoOriginal={accionActual.pedido}
				cancelar={cerrarAccion}
				guardar={editarPedido}
				onRefresh={refreshPedidos} // ✅ Pasar función de refresh
			/>

			<EliminarPedido
				mostrar={accionActual.accion === 'eliminar'}
				pedido={accionActual.pedido}
				cancelar={cerrarAccion}
				eliminar={eliminarPedido}
			/>

			<CancelarPedido
				mostrar={accionActual.accion === 'cancelar'}
				pedido={accionActual.pedido}
				cancelar={cerrarAccion}
				confirmarCancelacion={cancelarPedido}
			/>
		</Container>
	)
}

export default Pedidos